import useSyncedStorageState from 'hooks/useSyncedStorageState';
import { useUserReferralCode } from 'lib/referral';
import { REFERRAL_INCENTIVE_POPUP_STORAGE_KEY } from './constants';
import { useCallback, useEffect, useMemo, useState } from 'react';
import datetime from 'lib/datetime';

interface ReferralIncentivePopupOptions {
  key?: string;
  timeThreshold?: number;
  pathname?: string;
  isLoggedIn?: boolean;
  coolDownDays?: number;
  rememberMeDays?: number;
}

interface ReferralIncentivePopupState {
  startTime: number | null;
  dismissed: boolean;
  popupTriggered: boolean;
  snoozedUntil: number | null;
}

const defaultState: ReferralIncentivePopupState = {
  startTime: null,
  dismissed: false,
  popupTriggered: false,
  snoozedUntil: null,
};

const useReferralIncentivePopup = ({
  key,
  timeThreshold = 10_000,
  pathname,
  isLoggedIn,
  coolDownDays = 30,
  rememberMeDays = 3, // 3 days
}: ReferralIncentivePopupOptions = {}) => {
  const {
    referralCode,
    lastUsedAt,
    isLoading,
    error,
  } = useUserReferralCode();

  const isLoaded = !isLoading && !error;
  const [showPopup, setShowPopup] = useState(false);
  const [isSnoozed, setIsSnoozed] = useState(true);

  const isCheckoutResultPage = pathname === '/checkout/result';
  const isHomePage = pathname === '/';

  const isValidPage = isHomePage || isCheckoutResultPage;

  const { state, update, hydrated } = useSyncedStorageState(
    key
      ? `${REFERRAL_INCENTIVE_POPUP_STORAGE_KEY}_${key}`
      : REFERRAL_INCENTIVE_POPUP_STORAGE_KEY,
    defaultState,
    {
      type: 'local',
      channel: 'referral-incentive-popup',
      lockKey: 'referral-incentive-popup-lock',
    },
  );

  const hasOverdueCoolDown = useMemo(() => {
    if (lastUsedAt === null) return true;

    return (
      datetime().diff(datetime(lastUsedAt), 'day') >= coolDownDays
    );
  }, [coolDownDays, lastUsedAt]);

  useEffect(() => {
    if (!state.snoozedUntil) {
      setIsSnoozed(false);
      return;
    }

    const now = datetime();
    const snoozedEnd = datetime(state.snoozedUntil);
    const msRemaining = snoozedEnd.diff(now);

    if (msRemaining <= 0) {
      setIsSnoozed(false);
    } else {
      setIsSnoozed(true);

      const timer = setTimeout(() => {
        setIsSnoozed(false);
        update((prev) => ({
          ...prev,
          startTime: datetime().valueOf(),
        }));
      }, msRemaining);

      return () => clearTimeout(timer);
    }
  }, [state.snoozedUntil, update]);

  useEffect(() => {
    const canEligibilityChecked =
      hydrated &&
      isLoaded &&
      isLoggedIn &&
      referralCode &&
      !state.dismissed &&
      !state.popupTriggered &&
      hasOverdueCoolDown &&
      !isSnoozed;

    if (!canEligibilityChecked || !isValidPage) return;

    if (state.startTime === null) {
      update((prev) => ({
        ...prev,
        startTime: datetime().valueOf(),
      }));
      return;
    }

    if (isCheckoutResultPage) {
      update((prev) => ({ ...prev, popupTriggered: true }));
    } else if (isHomePage) {
      const elapsed = Date.now() - state.startTime;
      const remaining = Math.max(timeThreshold - elapsed, 0);

      const timer = setTimeout(() => {
        update((prev) => ({ ...prev, popupTriggered: true }));
      }, remaining);

      return () => clearTimeout(timer);
    }
  }, [
    hasOverdueCoolDown,
    hydrated,
    isCheckoutResultPage,
    isHomePage,
    isLoaded,
    isLoggedIn,
    isSnoozed,
    isValidPage,
    referralCode,
    state.dismissed,
    state.popupTriggered,
    state.startTime,
    timeThreshold,
    update,
  ]);

  // Trigger popup
  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (
      state.popupTriggered &&
      !state.dismissed &&
      isLoggedIn &&
      isLoaded &&
      !isSnoozed
    ) {
      timer = setTimeout(() => {
        setShowPopup(true);
      }, 1000);
    } else {
      setShowPopup(false);
    }

    return () => clearTimeout(timer);
  }, [
    isLoaded,
    isLoggedIn,
    isSnoozed,
    state.dismissed,
    state.popupTriggered,
  ]);

  const dismiss = useCallback(() => {
    update((prev) => ({ ...prev, dismissed: true }));
    setShowPopup(false);
  }, [update]);

  const rememberMe = useCallback(() => {
    const snoozedUntil = datetime()
      .add(rememberMeDays, 'day')
      .valueOf();
    update((prev) => ({
      ...prev,
      snoozedUntil,
      popupTriggered: false,
      dismissed: false,
      startTime: null,
    }));
    setShowPopup(false);
  }, [rememberMeDays, update]);

  return {
    showPopup,
    referralCode,
    dismiss,
    rememberMe,
  };
};

export default useReferralIncentivePopup;
